import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import FailedAnalysisWarningOverlay from 'shared/FailedAnalysisWarning';
import HumiditySaveConfirmationOverlay from './HumiditySaveConfirmationOverlay';
import InputGroupComponent from 'components/groups/InputGroup';
import Overlay from 'shared/Overlay';

import Validation from 'types/utils/validations';

import { VARIANT } from 'constants/inputTypes';
import { isInBound } from 'utils/validators';
import type HumidityAnalysisModel from 'productionOrders/models/HumidityAnalysis';

import useStores from 'utils/useStores';
import SelectOptionArray from 'types/model/selectOption';
import IHumidityAnalysis from 'types/model/IHumidityAnalysis';

// PLA-1025: el aviso no aporta ancho propio (width 0) y ocupa el de los campos
// (min-width 100%), así hace salto de línea en vez de ensanchar el modal.
const ParchmentRequiredHint = styled.p`
  width: 0;
  min-width: 100%;
  margin-top: 0.75rem;
  font-weight: bold;
`;

export interface HumidityAnalysisOverlayProps {
  identifier: string;
  validations: {
    humidity_green: Validation;
    water_activity: Validation;
    humidity_dry_parchment: Validation;
  };
  overlayStore?: any;
  humidityAnalysis?: IHumidityAnalysis | HumidityAnalysisModel;
  reasons: SelectOptionArray;
  showConfirmation?: boolean;
  confirmOnComplete?: boolean;
  onPrimaryClick?: (event: React.MouseEvent, values: {}) => any;
  onSecondaryClick?: (event: React.MouseEvent) => any;
}

function HumidityAnalysisOverlay({
  identifier,
  validations: { humidity_green, water_activity, humidity_dry_parchment },
  humidityAnalysis,
  reasons,
  showConfirmation = false,
  confirmOnComplete = false,
  onPrimaryClick,
  onSecondaryClick,
}: HumidityAnalysisOverlayProps) {
  const { overlayStore } = useStores();
  const [humidityGreen, setHumidityGreen] = useState('');
  const [humidityDryParchment, setHumidityDryParchment] = useState('');
  const [waterActivity, setWaterActivity] = useState('');
  const [pendingValues, setPendingValues] = useState<{} | null>(null);
  // Valores a la espera de confirmar el guardado permanente (flujo /lots).
  const [pendingSave, setPendingSave] = useState<{} | null>(null);

  useEffect(() => {
    if (humidityAnalysis) {
      setHumidityGreen(humidityAnalysis.humidity_green?.toString() || '');
      setHumidityDryParchment(humidityAnalysis.humidity_dry_parchment?.toString() || '');
      setWaterActivity(humidityAnalysis.water_activity?.toString() || '');

      if (humidityAnalysis.accepting_reason_id) {
        overlayStore.setReasonId(humidityAnalysis.accepting_reason_id);
        overlayStore.setValid(false);
        overlayStore.setOverrideValidation(true);
      }
    }
  }, []);

  // PLA-1025: pergamino es obligatorio para finalizar cuando el estándar del país lo
  // parametriza (los dos límites, igual que HumidityAnalysis#finalizable?).
  const parchmentRequired =
    humidity_dry_parchment.min != null && humidity_dry_parchment.max != null;

  // PLA-1025: con almendra y actividad del agua ya ingresadas, avisar que falta el
  // pergamino para finalizar; sin él el guardado queda parcial ("en proceso").
  const showParchmentRequiredHint =
    confirmOnComplete &&
    parchmentRequired &&
    humidityGreen?.length > 0 &&
    waterActivity?.length > 0 &&
    !(humidityDryParchment?.length > 0);

  useEffect(() => {
    const humidityGreenNumber = parseFloat(humidityGreen);
    const waterActivityNumber = parseFloat(waterActivity);
    const humidityDryParchmentNumber = parseFloat(humidityDryParchment);

    // PLA-1025: la medición se finaliza con humedad almendra y actividad del agua, más
    // la humedad pergamino si el estándar del país la parametriza (Nicaragua). Mientras
    // falte alguna se guarda como parcial ("en proceso"), sin popup de confirmación.
    // Mismo criterio que HumidityAnalysis#finalizable? en el backend.
    const isComplete =
      humidityGreen?.length > 0 &&
      waterActivity?.length > 0 &&
      (!parchmentRequired || humidityDryParchment?.length > 0);

    // PLA-1012: en /lots (confirmOnComplete) se permite guardado parcial: basta con un
    // valor ingresado. En el resto de flujos (p.ej. Decaf) se conserva el requisito original.
    let isFilled;
    if (confirmOnComplete) {
      isFilled =
        humidityGreen?.length > 0 ||
        waterActivity?.length > 0 ||
        humidityDryParchment?.length > 0;
    } else {
      isFilled = humidityGreen?.length > 0 && waterActivity?.length > 0;
      if (humidity_dry_parchment.min) {
        isFilled = isFilled && humidityDryParchment?.length > 0;
      }
    }

    let isValid =
      isInBound(humidityGreenNumber, humidity_green.min, humidity_green.max) &&
      isInBound(waterActivityNumber, water_activity.min, water_activity.max);

    // PLA-1012 v2: la humedad pergamino es opcional; sólo cuenta para la validez si el
    // usuario la ingresó. Si se deja vacía, el backend tampoco la compara contra el
    // estándar (PLA-1025): sólo un valor ingresado fuera de rango rechaza el lote.
    if (humidity_dry_parchment.min && humidityDryParchment?.length > 0) {
      isValid =
        isValid &&
        isInBound(
          humidityDryParchmentNumber,
          humidity_dry_parchment.min,
          humidity_dry_parchment.max
        );
    }

    overlayStore.setFilled(isFilled);
    overlayStore.setValid(isValid);
    overlayStore.setComplete(isComplete);

    if (overlayStore.overrideValidation) {
      overlayStore.setOverrideValidation(
        !(overlayStore.isFilled && overlayStore.isValid)
      );
    }
  }, [waterActivity, humidityGreen, humidityDryParchment]);

  const handleInputChange = (e, setter) => {
    setter(e.target.value);
  };

  const humidityGreenValidator = (e: React.ChangeEvent<HTMLInputElement>) =>
    isInBound(parseFloat(e.target.value), humidity_green.min, humidity_green.max);

  const humidityDryParchmentValidator = (e: React.ChangeEvent<HTMLInputElement>) =>
    isInBound(
      parseFloat(e.target.value),
      humidity_dry_parchment.min,
      humidity_dry_parchment.max
    );

  const waterActivityValidator = (e: React.ChangeEvent<HTMLInputElement>) =>
    isInBound(parseFloat(e.target.value), water_activity.min, water_activity.max);

  const isValueInBound = (value: string, bounds: Validation) =>
    value === '' || isInBound(parseFloat(value), bounds.min, bounds.max);

  const handleOnPrimaryClick = (event: React.MouseEvent) => {
    const currentValues = {
      accepting_reason_id: overlayStore.reasonId,
      humidity_green: humidityGreen,
      humidity_analysis_id: humidityAnalysis ? humidityAnalysis.id : null,
      humidity_dry_parchment: humidityDryParchment,
      water_activity: waterActivity,
    };

    if (onPrimaryClick) {
      // PLA-1025: con todos los valores requeridos, confirmar el guardado permanente (flujo /lots).
      if (confirmOnComplete && overlayStore.isComplete) {
        setPendingSave(currentValues);
      } else if (!overlayStore.isValid && showConfirmation) {
        setPendingValues(currentValues);
      } else {
        onPrimaryClick(event, currentValues);
      }
    }
  };

  const handleOnRejectionConfirmClick = (event: React.MouseEvent) => {
    const values = pendingValues;

    setPendingValues(null);

    if (onPrimaryClick && values) {
      onPrimaryClick(event, values);
    }
  };

  const handleOnRejectionCancelClick = () => setPendingValues(null);

  const handleOnSaveConfirmClick = (event: React.MouseEvent) => {
    const values = pendingSave;

    setPendingSave(null);

    if (onPrimaryClick && values) {
      onPrimaryClick(event, values);
    }
  };

  // Vuelve al formulario con los valores ingresados, igual que Cancel en el confirm nativo.
  const handleOnSaveCancelClick = () => setPendingSave(null);

  const handleOnSecondaryClick = (event) => {
    setHumidityGreen('');
    setWaterActivity('');
    setHumidityDryParchment('');
    overlayStore.setValid(false);
    overlayStore.setFilled(false);
    overlayStore.setComplete(false);
    overlayStore.setOverrideValidation(false);
    overlayStore.setReasonId(null);

    if (onSecondaryClick) {
      onSecondaryClick(event);
    }
  };

  if (pendingSave) {
    return (
      <HumiditySaveConfirmationOverlay
        identifier={identifier}
        values={{ humidityGreen, waterActivity, humidityDryParchment }}
        onConfirmClick={handleOnSaveConfirmClick}
        onCancelClick={handleOnSaveCancelClick}
      />
    );
  }

  if (pendingValues) {
    return (
      <FailedAnalysisWarningOverlay
        identifier={identifier}
        lead={I18n.translate('failed_analysis_warning.lead_humidity')}
        onConfirmClick={handleOnRejectionConfirmClick}
        onCancelClick={handleOnRejectionCancelClick}
      />
    );
  }

  return (
    <Overlay
      id={identifier}
      title={`${identifier} Humidity`}
      primaryDisabled={!overlayStore.isFilled}
      primaryText={I18n.translate('buttons.save')}
      onPrimaryClick={handleOnPrimaryClick}
      secondaryText={I18n.translate('buttons.close')}
      onSecondaryClick={handleOnSecondaryClick}
      reasons={reasons}
    >
      <div className='l-item-stack-horizontal'>
        <InputGroupComponent
          data-cy={'humidity_green'}
          label={I18n.translate('attributes.humidity_green')}
          variant={VARIANT.DECIMAL}
          append='%'
          value={humidityGreen}
          validator={humidityGreenValidator}
          externalValid={isValueInBound(humidityGreen, humidity_green)}
          onChange={(e) => handleInputChange(e, setHumidityGreen)}
          step='0.01'
        />

        <InputGroupComponent
          data-cy={'water_activity'}
          label={I18n.translate('attributes.water_activity')}
          variant={VARIANT.DECIMAL}
          value={waterActivity}
          validator={waterActivityValidator}
          externalValid={isValueInBound(waterActivity, water_activity)}
          onChange={(e) => handleInputChange(e, setWaterActivity)}
          step='0.01'
        />

        <InputGroupComponent
          data-cy={'humidity_parchment_short'}
          label={I18n.translate('attributes.humidity_parchment_short')}
          variant={VARIANT.DECIMAL}
          append='%'
          value={humidityDryParchment}
          validator={humidityDryParchmentValidator}
          externalValid={isValueInBound(humidityDryParchment, humidity_dry_parchment)}
          onChange={(e) => handleInputChange(e, setHumidityDryParchment)}
          step='0.01'
        />
      </div>

      {showParchmentRequiredHint && (
        <ParchmentRequiredHint
          className='small text-red'
          data-cy='humidity_parchment_required_hint'
        >
          {I18n.translate('humidity_analysis_overlay.parchment_required_hint')}
        </ParchmentRequiredHint>
      )}
    </Overlay>
  );
}

export default observer(HumidityAnalysisOverlay);
